import * as Sentry from '@sentry/browser';
import { AxiosError, AxiosHeaders, AxiosRequestConfig, isAxiosError } from 'axios';

import { CONFIG } from '@/config/config.ts';

const CAPTURED_MARKER = '__glitchtipCaptured__';
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 50;
const MAX_STRING_LENGTH = 4_000;
const MAX_VALUE_DEPTH = 4;
const REDACTED_VALUE = '[REDACTED]';
const TRUNCATED_VALUE_SUFFIX = '...[truncated]';
const REDACTED_KEY_PARTS = [
  'auth',
  'authorization',
  'cookie',
  'csrf',
  'jsession',
  'password',
  'secret',
  'session',
  'set-cookie',
  'token',
] as const;

type CapturableError = {
  [CAPTURED_MARKER]?: boolean;
};

type CaptureAppExceptionContext = {
  componentStack?: string;
  contextName?: string;
};

export const initErrorMonitoring = (): void => {
  if (!CONFIG.GLITCHTIP_DSN) {
    return;
  }

  Sentry.init({
    attachStacktrace: true,
    dsn: CONFIG.GLITCHTIP_DSN,
    environment: CONFIG.ENVIRONMENT,
    maxBreadcrumbs: 50,
    normalizeDepth: MAX_VALUE_DEPTH,
  });
};

export const setAppVersion = (version: string): void => {
  if (!CONFIG.GLITCHTIP_DSN) {
    return;
  }

  Sentry.setTag('app.version', version);
};

export const captureAppException = (error: unknown, context?: CaptureAppExceptionContext): void => {
  if (!CONFIG.GLITCHTIP_DSN || hasBeenCaptured(error)) {
    return;
  }

  markAsCaptured(error);

  Sentry.withScope(scope => {
    if (context?.contextName) {
      scope.setContext('app', { context: context.contextName });
    }

    if (context?.componentStack) {
      scope.setContext('react-error-boundary', {
        componentStack: truncateString(context.componentStack),
      });
    }

    if (isAxiosError(error)) {
      applyAxiosErrorScope(scope, error);
    }

    Sentry.captureException(error);
  });
};

const applyAxiosErrorScope = (scope: Sentry.Scope, error: AxiosError): void => {
  const requestContext = buildRequestContext(error);
  const responseContext = buildResponseContext(error);
  const axiosContext = removeUndefined({
    code: error.code,
    hasResponse: Boolean(error.response),
    message: error.message,
    name: error.name,
    status: error.response?.status,
  });

  if (requestContext) {
    scope.setContext('http-request', requestContext);
    scope.setExtra('http_request', requestContext);
  }

  if (responseContext) {
    scope.setContext('http-response', responseContext);
    scope.setExtra('http_response', responseContext);
  }

  scope.setContext('axios', axiosContext);
  scope.setExtra('axios_error', axiosContext);

  if (requestContext?.method) {
    scope.setTag('http.method', requestContext.method);
  }

  if (requestContext?.url) {
    scope.setTag('http.url', requestContext.url);
  }

  if (error.response?.status) {
    scope.setTag('http.status_code', String(error.response.status));
    if (error.response.status < 500) {
      scope.setLevel('warning');
    }
  }
};

const buildRequestContext = (error: Parameters<typeof applyAxiosErrorScope>[1]) => {
  const config = error.config;

  if (!config) {
    return undefined;
  }

  return removeUndefined({
    baseUrl: config.baseURL,
    body: sanitizeValue(config.data),
    headers: sanitizeHeaders(config.headers),
    method: config.method?.toUpperCase(),
    params: sanitizeValue(config.params),
    timeout: config.timeout,
    url: resolveRequestUrl(config),
  });
};

const buildResponseContext = (error: Parameters<typeof applyAxiosErrorScope>[1]) => {
  if (!error.response) {
    return undefined;
  }

  return removeUndefined({
    body: sanitizeValue(error.response.data),
    headers: sanitizeHeaders(error.response.headers),
    status: error.response.status,
    statusText: error.response.statusText,
  });
};

const hasBeenCaptured = (error: unknown): boolean => {
  if (!isCapturableError(error)) {
    return false;
  }

  return Boolean(error[CAPTURED_MARKER]);
};

const markAsCaptured = (error: unknown): void => {
  if (!isCapturableError(error) || !Object.isExtensible(error)) {
    return;
  }

  error[CAPTURED_MARKER] = true;
};

const isCapturableError = (error: unknown): error is CapturableError => {
  return typeof error === 'object' && error !== null;
};

const resolveRequestUrl = (config?: AxiosRequestConfig): string | undefined => {
  if (!config?.url) {
    return undefined;
  }

  try {
    return new URL(config.url, config.baseURL).toString();
  } catch {
    if (config.baseURL) {
      return `${config.baseURL}${config.url}`;
    }

    return config.url;
  }
};

const sanitizeHeaders = (headers: unknown): unknown => {
  if (headers instanceof AxiosHeaders) {
    return sanitizeValue(headers.toJSON());
  }

  if (headers && typeof headers === 'object' && 'toJSON' in headers) {
    const { toJSON } = headers as { toJSON?: () => unknown };

    if (typeof toJSON === 'function') {
      return sanitizeValue(toJSON());
    }
  }

  return sanitizeValue(headers);
};

const sanitizeValue = (value: unknown, depth = 0): unknown => {
  if (value === undefined || value === null) {
    return value;
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    return truncateString(value.toString());
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const entries = Array.from(value.entries()).slice(0, MAX_OBJECT_KEYS);

    return Object.fromEntries(
      entries.map(([key, entryValue]) => [key, sanitizeValue(entryValue, depth + 1)]),
    );
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return {
      lastModified: value.lastModified,
      name: value.name,
      size: value.size,
      type: value.type,
    };
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return {
      size: value.size,
      type: value.type,
    };
  }

  if (value instanceof ArrayBuffer) {
    return { byteLength: value.byteLength, type: 'ArrayBuffer' };
  }

  if (ArrayBuffer.isView(value)) {
    return { byteLength: value.byteLength, type: value.constructor.name };
  }

  if (depth >= MAX_VALUE_DEPTH) {
    return `[${Array.isArray(value) ? 'Array' : 'Object'}]`;
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map(item => sanitizeValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value).slice(0, MAX_OBJECT_KEYS);

    return Object.fromEntries(
      entries.map(([key, entryValue]) => {
        return [key, shouldRedactKey(key) ? REDACTED_VALUE : sanitizeValue(entryValue, depth + 1)];
      }),
    );
  }

  return Object.prototype.toString.call(value);
};

const shouldRedactKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();

  return REDACTED_KEY_PARTS.some(part => normalizedKey.includes(part));
};

const truncateString = (value: string): string => {
  if (value.length <= MAX_STRING_LENGTH) {
    return value;
  }

  return `${value.slice(0, MAX_STRING_LENGTH)}${TRUNCATED_VALUE_SUFFIX}`;
};

const removeUndefined = <T extends Record<string, unknown>>(value: T): T => {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined),
  ) as T;
};
